import { useLazyQuery } from "@apollo/client";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { Download } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { Stack } from "types/generated";
import { downloadFileFromURL } from "utils/file";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";

import { GET_STACK_RUN_TRACE_DOWNLOAD_URL } from "./gql";
import styles from "./styles.module.css";

const JAEGER_STEPS = [
  "Open Jaeger UI and go to the Search page.",
  "Switch to the JSON File tab.",
  "Drop in the downloaded file to open the run in the timeline view.",
];

type ExportTracesDrawerProps = {
  stackId: string;
  runId: string;
  partial: boolean;
};

const ExportTracesDrawer = createDrawer(({ stackId, runId, partial }: ExportTracesDrawerProps) => {
  const { reportError } = useTypedContext(FlashContext);
  const trackSegmentAnalyticsEvent = useAnalytics();

  const title = partial ? "Export partial traces" : "Export traces";

  const description = partial
    ? "The trace downloads as a single Jaeger JSON file holding the spans captured so far."
    : "The trace downloads as a single Jaeger JSON file holding every span from this run.";

  const [getTraceDownloadURL, { loading }] = useLazyQuery<{ stack: Stack }>(
    GET_STACK_RUN_TRACE_DOWNLOAD_URL,
    {
      fetchPolicy: "network-only",
      context: { suppressFlashError: true },
    }
  );

  const handleDownload = useCallback(async () => {
    const { data, error } = await getTraceDownloadURL({ variables: { stackId, runId } });

    if (error) {
      reportError({
        title: "Export failed",
        message: "Failed to export traces. Please try again later.",
      });
      return;
    }

    const url = data?.stack?.run?.otelTraceForJaegerDownloadURL?.url;

    if (!url) {
      reportError({
        message: "Traces are not available for this run.",
      });
      return;
    }

    downloadFileFromURL(url);

    trackSegmentAnalyticsEvent("Run Observability - export traces");
  }, [getTraceDownloadURL, stackId, runId, reportError, trackSegmentAnalyticsEvent]);

  return (
    <DrawerSimple>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title={title} />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody fullHeight className={styles.body}>
        <Banner variant="info">{description}</Banner>
        <Box direction="column" gap="large">
          <Typography tag="h3" variant="p-t7">
            Open it in Jaeger
          </Typography>
          <Typography tag="div" variant="p-body3-multiline">
            <ol className={styles.steps}>
              {JAEGER_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </Typography>
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <DrawerFooterActions>
          <Button variant="primary" startIcon={Download} onPress={handleDownload} loading={loading}>
            Download trace
          </Button>
        </DrawerFooterActions>
      </DrawerFooter>
    </DrawerSimple>
  );
});

export const showExportTracesDrawer = createDrawerTrigger(ExportTracesDrawer);
